/**
 * <NotificationChannelsList> — les notifications, une ligne par type, deux canaux (molecule).
 *
 * Chaque ligne est un `<SettingRow>` sans geste, qui porte dans son emplacement de fin une bascule
 * par canal réglable : la notification sur le téléphone, l'e-mail. Un type qui n'a qu'un canal
 * laisse l'autre colonne vide, pour que les bascules restent alignées d'une ligne à l'autre. La
 * tête de liste nomme les deux colonnes une fois, par leur glyphe.
 *
 * La liste vient du serveur, filtrée par rôle : le composant ne connaît aucun type.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Divider } from "../../atoms/Divider"
import { Row } from "../../atoms/containers/Row"
import { Icon, type IconName } from "../../atoms/icons/Icon"
import { Switch } from "../../atoms/Switch"
import { SettingRow } from "../SettingRow"
import { type UINotificationSetting } from "../../../ports/primary/ui/types"

export type { UINotificationSetting }

export type NotificationChannel = "push" | "email"

export type NotificationChannelsListProps = {
    items: UINotificationSetting[]
    onToggle: (slug: string, channel: NotificationChannel, next: boolean) => void
    /** Envoi en cours : les bascules ne répondent plus. */
    disabled?: boolean
    testID?: string
}

const CHANNELS: Array<{ key: NotificationChannel; iconName: IconName; name: string }> = [
    { key: "push", iconName: "bell", name: "notification" },
    { key: "email", iconName: "envelope", name: "e-mail" },
]

/** La largeur d'une colonne de canal : celle de la piste d'un `<Switch>`. */
const CHANNEL_WIDTH = 48

export function NotificationChannelsList(props: NotificationChannelsListProps) {
    const { items, onToggle, disabled = false, testID } = props

    return (
        <View testID={testID}>
            <Row gap="3" justify="end" style={styles.head}>
                {CHANNELS.map((channel) => (
                    <View key={channel.key} style={styles.channel} accessibilityLabel={channel.name}>
                        <Icon name={channel.iconName} size={20} color="text-muted" />
                    </View>
                ))}
            </Row>
            {items.map((item, index) => (
                <React.Fragment key={item.slug}>
                    {index > 0 && <Divider spacing="1" />}
                    <SettingRow
                        label={item.name}
                        trailing={
                            <Row gap="3">
                                {CHANNELS.map((channel) => {
                                    const checked = item[channel.key]
                                    return (
                                        <View key={channel.key} style={styles.channel}>
                                            {checked != null && (
                                                <Switch
                                                    checked={checked}
                                                    disabled={disabled}
                                                    accessibilityLabel={`${item.name}, ${channel.name}`}
                                                    onChange={(next) => onToggle(item.slug, channel.key, next)}
                                                />
                                            )}
                                        </View>
                                    )
                                })}
                            </Row>
                        }
                    />
                </React.Fragment>
            ))}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    head: { paddingBottom: theme.spacing["1"] },
    channel: { width: CHANNEL_WIDTH, alignItems: "center" },
}))
